/**
 * ListPageLayout.tsx
 *
 * The three-column list shell — filters, content, record detail — shared by 36
 * pages. All the narrow-width behaviour lives here precisely because those 36 call
 * sites pass their panels as node props: the phone and tablet branches can be added
 * without touching a single page.
 *
 * ── Two breakpoints, not one ────────────────────────────────────────────────────
 * The filter panel leaves the flow below `md`; the right panel below `lg`. A single
 * `md` branch would look correct and be wrong: at 768px the content column would be
 * left with roughly 250px once a 400px detail panel and the collapsed filter rail
 * are subtracted, which is below the width floor the plan sets for keeping a panel
 * in flow. So the tablet band keeps filters inline and sends the detail panel
 * off-canvas.
 *
 *   ≥1024      filters inline, detail inline — identical to before this change
 *   768–1023   filters inline, detail off-canvas from the right
 *   <768       both off-canvas, single column, controls in a sticky bar
 *
 * ── How the detail sheet knows to open ──────────────────────────────────────────
 * Preferred: the page passes `detailOpenSignal`, a value it changes each time the
 * user picks a record. Changing it is the request to open.
 *
 * Fallback, for the consumers that pass no signal: the node appearing where there
 * was none is read as the same request. That inference is what SCRUM-1383 was —
 * `rightPanel != null` is a *boolean*, so once a page has selected anything it stays
 * true forever, the effect's dependencies never change again, and every subsequent
 * row tap is a no-op that opens nothing and reports no error. A page that never
 * clears its selection (PatientsPage did not) could open this sheet exactly once per
 * page load. Passing a signal is what makes a re-selection observable at all; the
 * inference cannot see one by construction, so prefer the signal on any page where
 * the same record can be picked twice.
 *
 * Either way a Details control stays in the bar so a dismissed sheet can be reopened
 * without re-picking the row.
 *
 * Both transitions use the JS breakpoint hook rather than `max-lg:` class escapes.
 * A printed page lays out near 816px, so a class escape at the `lg` boundary would
 * fire during printing and drop the detail panel from printed output.
 */
import { SidebarProvider } from "@/components/ui/sidebar";
import { Filter, Loader2, PanelRightOpen } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { useIsBelow } from "@/hooks/useBreakpoint";
import { OffCanvasPanel, OffCanvasRegion } from "./OffCanvasPanel";

interface ListPageLayoutProps {
  filterSidebar: React.ReactNode;
  children: React.ReactNode;
  rightPanel?: React.ReactNode;
  loading?: boolean;
  /**
   * Change this each time the user picks a record to request the detail sheet open
   * below `lg`. A counter incremented in the row-click handler is the usual shape.
   * Omit it to keep the node-presence inference described above — which cannot detect
   * re-selecting an already-selected record.
   */
  detailOpenSignal?: number;
}

export function ListPageLayout({
  filterSidebar,
  children,
  rightPanel,
  loading = false,
  detailOpenSignal,
}: ListPageLayoutProps) {
  const filtersOffCanvas = useIsBelow("md");
  const detailOffCanvas = useIsBelow("lg");

  const hasFilters = filterSidebar != null;
  const hasDetail = rightPanel != null;

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const hadDetail = useRef(hasDetail);
  // Seeded with the mount-time value so the first run compares equal and an
  // already-selected record does not spring the sheet open on arrival.
  const lastSignal = useRef(detailOpenSignal);
  useEffect(() => {
    // Both refs are kept in sync even while inline, so growing past `lg` with a record
    // already selected and shrinking back does not spring the sheet open on resize.
    if (detailOffCanvas) {
      const requested =
        detailOpenSignal === undefined
          ? hasDetail && !hadDetail.current // node-presence fallback
          : detailOpenSignal !== lastSignal.current;

      if (hasDetail && requested) setDetailOpen(true);
      else if (!hasDetail) setDetailOpen(false);
    }
    hadDetail.current = hasDetail;
    lastSignal.current = detailOpenSignal;
  }, [hasDetail, detailOffCanvas, detailOpenSignal]);

  // A panel that is back in the flow must not also be open off-canvas.
  useEffect(() => {
    if (!filtersOffCanvas) setFiltersOpen(false);
  }, [filtersOffCanvas]);

  useEffect(() => {
    if (!detailOffCanvas) setDetailOpen(false);
  }, [detailOffCanvas]);

  const showFiltersControl = filtersOffCanvas && hasFilters;
  const showDetailControl = detailOffCanvas && hasDetail;

  return (
    <OffCanvasRegion>
      <SidebarProvider
        // max-md: releases the viewport lock so list pages scroll as a document
        // on phones (pairs with the same relaxation in Layout.tsx). md+ unchanged.
        className="h-full min-h-0 overflow-hidden max-md:h-auto max-md:overflow-visible"
        style={
          {
            "--sidebar-width": "16rem",
            "--sidebar-width-icon": "3.5rem",
          } as React.CSSProperties
        }
      >
        {!filtersOffCanvas && filterSidebar}

        <div className="flex-1 min-w-0 overflow-hidden flex flex-col max-md:overflow-visible">
          {(showFiltersControl || showDetailControl) && (
            <div className="sticky top-0 z-30 flex shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
              {showFiltersControl && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Filter className="h-4 w-4" />}
                  onClick={() => setFiltersOpen(true)}
                >
                  Filters
                </Button>
              )}

              {showDetailControl && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="ml-auto"
                  leftIcon={<PanelRightOpen className="h-4 w-4" />}
                  onClick={() => setDetailOpen(true)}
                >
                  Details
                </Button>
              )}
            </div>
          )}

          {loading ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            children
          )}
        </div>

        {!detailOffCanvas && rightPanel}

        {showFiltersControl && (
          <OffCanvasPanel
            id="list-filters"
            isOpen={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            side="left"
            title="Filters"
          >
            {filterSidebar}
          </OffCanvasPanel>
        )}

        {showDetailControl && (
          <OffCanvasPanel
            id="list-detail"
            isOpen={detailOpen}
            onClose={() => setDetailOpen(false)}
            side="right"
            title="Details"
            size="wide"
          >
            {rightPanel}
          </OffCanvasPanel>
        )}
      </SidebarProvider>
    </OffCanvasRegion>
  );
}
